import { APIRequest } from '@lynx/client-core/src/api'
import { BasicButton } from '@lynx/client-core/src/components'
import { LinkIcon, ShareIcon } from '@lynx/client-core/src/components/Icons'
import { useThunkDispatch } from 'hooks'
import { sleep } from 'modules/utils'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { ApplicationState } from 'store'
import { endConnection, getUsers } from 'store/session'
import { SidebarProps } from './interfaces'
import css from './Sidebar.module.scss'
import { SidebarItem } from './SidebarItem'

export const Sidebar: React.FC<SidebarProps> = ({ showQr }): React.ReactElement => {
	const {
		session: { users, channelId, qr, url }
	} = useSelector((state: ApplicationState) => state)
	const [open, setOpen] = useState<boolean>(true)
	const [ended, setEnded] = useState<boolean>(false)
	const dispatchThunk = useThunkDispatch()
	const dispatch = useDispatch()
	const { t } = useTranslation()

	useEffect(() => {
		const mount = async (): Promise<void> => {
			await sleep(1000)
			setOpen(false)
		}
		mount()
	}, [])

	useEffect(() => {
		const onEnded = async (): Promise<void> => {
			if (!ended) {
				return
			}
			//Drop VNC connection
			dispatch(endConnection())
			//Let other connections know this is happening
			await APIRequest.Channels.sendMessage(channelId, 'vnc:close', {})
			//Delete channel
			await APIRequest.Channels.delete(channelId)
			setOpen(false)
		}
		onEnded()
	}, [ended])

	useEffect(() => {
		const handleGetUser = async (): Promise<void> => {
			if (channelId) {
				await dispatchThunk(getUsers())
			}
		}
		handleGetUser()
	}, [channelId])

	const toggleSidebar = (): void => {
		setOpen(!open)
	}

	const toggleQR = (): void => {
		setOpen(false)
		showQr()
	}

	const exit = (): void => {
		setEnded(true)
	}

	const filteredUsers = users.filter((value, index, self) => index === self.findIndex((t) => t.userId === value.userId))

	const itemElements = filteredUsers.map((item, index) => {
		if (['unknown', 'anonymous', 'Admin'].includes(item.displayName)) {
			return null
		}
		return (
			<li key={index}>
				<SidebarItem route="" channelId={channelId} userId={item.userId} name={item.displayName} active={false} />
			</li>
		)
	})

	const styles = [css['container']]
	!open && styles.push(css['container--closed'])

	const stylesOpen = [css['openButton']]
	open && stylesOpen.push(css['openButtonSidebarOpened'])

	return (
		<React.Fragment>
			<div className={styles.join(' ')}>
				<div className={css['share']}>
					<img alt="Join QR" width="175" src={qr} onClick={toggleQR} />
					<BasicButton
						icon={LinkIcon}
						onClick={(): void => {
							navigator.clipboard.writeText(url)
						}}
					>
						{t('pages.litb.copyLink')}
					</BasicButton>
				</div>
				<div className={css['userList']}>
					<ul>{itemElements}</ul>
				</div>
				<div className={css['buttons-container']}>
					<BasicButton variant="danger" size="xl" className={css['button']} onClick={exit}>
						{t('pages.litb.endSession')}
					</BasicButton>
				</div>
				<button onClick={toggleSidebar} className={stylesOpen.join(' ')}>
					<ShareIcon width={48} height={48} />
				</button>
			</div>
		</React.Fragment>
	)
}
